import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { LucideAngularModule, ChevronDown, ChevronUp, Info } from 'lucide-angular';
import { LessonPlanService } from '../../services/lesson-plan.service';
import { THEME_COLOR, TechRatio } from '../../services/lesson-plan.models';
import { INSTRUCTIONAL_MODELS, InstructionalModel } from '../../services/instructional-models';
import { ModelInfoPopoverComponent } from '../../components/model-info-popover/model-info-popover';

@Component({
  selector: 'app-step-settings',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, ModelInfoPopoverComponent],
  templateUrl: './step-settings.html',
  styleUrl: './step-settings.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepSettingsComponent {
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly destroyRef = inject(DestroyRef);

  @Output() readonly back = new EventEmitter<void>();
  @Output() readonly next = new EventEmitter<void>();

  protected readonly themeColor = THEME_COLOR;
  protected readonly inputData$ = this.lessonPlanService.inputData$;
  protected readonly error$ = this.lessonPlanService.error$;

  protected readonly durations: readonly { value: string; label: string }[] = [
    { value: '30', label: '30 min' },
    { value: '45', label: '45 min' },
    { value: '60', label: '60 min' },
    { value: '90', label: '90 min' },
  ];

  protected readonly icons = {
    ChevronDown,
    ChevronUp,
    Info,
  };

  /** Instructional models list */
  protected readonly instructionalModels = INSTRUCTIONAL_MODELS;

  /** Number of models to show initially */
  private readonly VISIBLE_MODELS_COUNT = 4;

  /** Show more toggle for instructional models */
  private readonly showMoreModels$$ = new BehaviorSubject<boolean>(false);
  protected readonly showMoreModels$ = this.showMoreModels$$.asObservable();

  /** Visible instructional models (respecting show more toggle) */
  protected readonly visibleModels$: Observable<InstructionalModel[]> = this.showMoreModels$.pipe(
    map((showMore) => showMore ? INSTRUCTIONAL_MODELS : INSTRUCTIONAL_MODELS.slice(0, this.VISIBLE_MODELS_COUNT))
  );

  /** Check if there are hidden models */
  protected readonly hasHiddenModels = INSTRUCTIONAL_MODELS.length > this.VISIBLE_MODELS_COUNT;

  /** Currently selected model for popover display */
  private readonly popoverModel$$ = new BehaviorSubject<InstructionalModel | null>(null);
  protected readonly popoverModel$ = this.popoverModel$$.asObservable();

  /** Popover open state */
  private readonly isPopoverOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly isPopoverOpen$ = this.isPopoverOpen$$.asObservable();

  /** Tech Access options */
  protected readonly techRatioOptions: readonly { value: TechRatio; label: string; description: string }[] = [
    { value: '1:1', label: '1:1 Devices', description: 'Every student has their own device (laptop, tablet, or Chromebook)' },
    { value: 'shared', label: 'Shared / Carts', description: 'Students share devices in pairs or small groups, or use a device cart' },
    { value: 'teacher', label: 'Teacher Only', description: 'Teacher controls the technology via projector or interactive display' },
    { value: 'none', label: 'Low Tech / None', description: 'Lesson does not require any digital technology or devices' },
  ];

  /** Show advanced tech details toggle */
  private readonly showAdvancedTech$$ = new BehaviorSubject<boolean>(false);
  protected readonly showAdvancedTech$ = this.showAdvancedTech$$.asObservable();

  protected updateInstructionalModel(id: string): void {
    const currentData = this.lessonPlanService.inputData;
    // Toggle off if already selected
    if (currentData.instructionalModel === id) {
      this.lessonPlanService.updateInputData({ instructionalModel: null });
    } else {
      this.lessonPlanService.updateInputData({ instructionalModel: id });
    }
  }

  protected openModelInfo(model: InstructionalModel, event: Event): void {
    event.stopPropagation();
    this.popoverModel$$.next(model);
    this.isPopoverOpen$$.next(true);
  }

  protected closeModelInfo(): void {
    this.isPopoverOpen$$.next(false);
    this.popoverModel$$.next(null);
  }

  protected toggleShowMoreModels(): void {
    this.showMoreModels$$.next(!this.showMoreModels$$.value);
  }

  protected updateDuration(value: string): void {
    this.lessonPlanService.updateInputData({ duration: value });
  }

  protected updateTechRatio(value: TechRatio): void {
    const currentData = this.lessonPlanService.inputData;
    // Toggle off if already selected
    if (currentData.techRatio === value) {
      this.lessonPlanService.updateInputData({ techRatio: null });
    } else {
      this.lessonPlanService.updateInputData({ techRatio: value });
    }
    // Reset advanced section when changing ratio
    this.showAdvancedTech$$.next(false);
  }

  protected toggleShowAdvancedTech(): void {
    this.showAdvancedTech$$.next(!this.showAdvancedTech$$.value);
  }

  protected updateTechDetail(field: keyof typeof this.lessonPlanService.inputData.techDetails, value: boolean): void {
    const currentDetails = { ...this.lessonPlanService.inputData.techDetails };
    currentDetails[field] = value;
    this.lessonPlanService.updateInputData({ techDetails: currentDetails });
  }

  protected onBack(): void {
    this.back.emit();
  }

  protected onNext(): void {
    // Complete settings step and update status before navigating
    this.lessonPlanService
      .completeSettingsStep()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.next.emit();
        },
        error: () => {
          // Error is already handled by the service
        },
      });
  }
}
